import { map, of } from 'rxjs';
import {
  readEntitiesByIDs,
  watchEntitiesByIDs,
  readAllEntities,
  watchAllEntities,
} from '@adornis/bh-util/server/entity-reads.js';
import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int, ID } from '@adornis/baseql/baseqlTypes.js';
import { getMailTemplateForInstitution } from '@adornis/buchungsheld-db/api/mail-templates.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getBHPaymentByID } from '@adornis/buchungsheld-db/api/payment.js';
import { getUserByID } from '@adornis/buchungsheld-db/api/users.js';
import { context, serverContext } from '@adornis/baseql/server/context.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { runInContext } from '@adornis/baseql/server/server.js';
import { selectionSet, type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { scopedCollection } from '@adornis/bh-util/server/scoped-collection.js';
import { getCurrentUserServer } from '@adornis/bh-util/util.js';
import { getAllBundlesByInstitution } from '@adornis/buchungsheld-db/api/bundle.js';
import { getParticipationsByID } from '@adornis/buchungsheld-db/api/participation-helper.js';
import { getStammdatenByUserAndInstitution } from '@adornis/buchungsheld-db/api/stammdaten.js';
import { Einzelberatung } from '@adornis/buchungsheld-db/entities/einzelberatung.js';
import {
  getEmailVarsFromEinzelberatung,
  getEmailVarsFromUser,
  getEmailVarsFromWorkshop,
} from '@adornis/buchungsheld-db/entities/mail-helper.js';
import { InstitutionMailTemplate, MailTemplateType } from '@adornis/buchungsheld-db/entities/mail-templates.js';
import type { Participation } from '@adornis/buchungsheld-db/entities/Participation.js';
import { Product } from '@adornis/buchungsheld-db/entities/products.js';
import { UserStammdaten } from '@adornis/buchungsheld-db/entities/stammdaten.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import { ParticipationState } from '@adornis/buchungsheld-db/enums/enums.js';
import { Permission } from '@adornis/buchungsheld-db/enums/roles.js';
import { getProductsBestBundled } from '@adornis/buchungsheld-db/global-helpers.js';
import { assembleInstitutionMail } from '@adornis/buchungsheld-db/mails/assemble-mail.js';
import { fileAttachment } from '@adornis/buchungsheld-db/mails/file-attachment.js';
import { sendInstitutionEmail } from '@adornis/buchungsheld-db/mails/sendInstitutionMail.js';
import {
  sendWhatsappTemplateMessage,
  writeNotificationSendLog,
} from '@adornis/buchungsheld-db/notification/_helper.js';
import { notifyBestaetigungWorkshopRegistrierung } from '@adornis/buchungsheld-db/notification/bestaetigung-workshop-registrierung.js';
import { printComponent } from '@adornis/buchungsheld-db/print-with-user-session.js';
import { isDefined } from '@adornis/buchungsheld-db/util.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { ComponentParamaterType } from '@adornis/whatsapp/server/types.js';
import type { TemplateResult } from 'lit';
import { BHIKHOrderNr } from '../payment/BHIKHOrderNr.js';
import { IKHStammdaten } from '../stammdaten/ikh-stammdaten-plugin.js';
import { IKHInvoiceReceiverEnum } from '../payment/invoice-receiver.js';
import { createPaymentFromParticipations } from '../payment/helper.js';
import { IKHInvoice, getProductPrice, buildIkhInvoiceFileName } from '../payment/ikh-payment-plugin.js';

async function notifiyIkhInvoice(
  institutionID: string,
  user: User,
  product: Product,
  template: MailTemplateType,
  invoiceLink: string,
  fileName: string,
  receiverMail: string,
  cc: Maybe<string>,
  invoiceFileID: string,
) {
  const mailTemplate = await getMailTemplateForInstitution(
    institutionID,
    template,
  )(InstitutionMailTemplate.allFields as any);

  let whatsappSucceeded = false;
  let whatsappError = 'whatsapp not attempted because it is disabled or the user is not opted in';
  if (user && user.whatsappNotification && user.whatsappPhone && mailTemplate.whatsappNotification) {
    const { createSignedFileServeUrl } = await import('@adornis/buchungsheld-db/server/signed-file-url.js');
    const result = await sendWhatsappTemplateMessage({
      institutionID,
      user: { _id: user._id, whatsappPhone: user.whatsappPhone },
      mailTemplateType: template,
      parameters: [
        InstitutionMailTemplate.mapFirstNameVar(user) || '-',
        InstitutionMailTemplate.mapLastNameVar(user) || '-',
      ],
      headerParameters: [
        {
          type: ComponentParamaterType.IMAGE,
          image: { link: createSignedFileServeUrl(invoiceFileID, fileName, 86400) },
        },
      ],
    });
    whatsappSucceeded = result.ok;
    whatsappError = result.error ?? 'unknown whatsapp error';
    logger.info({ result }, 'whatsapp result');
  }

  if (!whatsappSucceeded && !mailTemplate.emailNotification) {
    logger.error(
      { institutionID, userID: user._id, mailTemplateType: template, whatsappError },
      'IKH invoice notification dropped because whatsapp did not succeed and email is disabled',
    );
    try {
      await writeNotificationSendLog({
        institutionID,
        userID: user._id,
        mailTemplateType: template,
        status: 'failed',
        errorMessage: `IKH invoice notification dropped: ${whatsappError}; email notification disabled`,
      });
    } catch (logError) {
      logger.error(
        { err: logError, institutionID, userID: user._id, mailTemplateType: template },
        'failed to write IKH invoice notification send log',
      );
    }
  }
  if (whatsappSucceeded || !mailTemplate.emailNotification) return false;

  const emailVars = (() => {
    if (product instanceof Workshop)
      return getEmailVarsFromWorkshop(product, {
        title: user.title,
        firstName: user.firstName,
        lastName: user.lastName,
      });
    else if (product instanceof Einzelberatung)
      return getEmailVarsFromEinzelberatung(product, {
        title: user.title,
        firstName: user.firstName,
        lastName: user.lastName,
      });
    else return getEmailVarsFromUser({ title: user.title, firstName: user.firstName, lastName: user.lastName });
  })();

  const { content: mailContent } = await assembleInstitutionMail({ template: mailTemplate, infos: emailVars });

  if (!mailContent) throw new Error('could not build email');
  await sendInstitutionEmail({
    email: receiverMail,
    subject: mailTemplate.subject,
    content: mailContent as TemplateResult<1>,
    institutionID,
    attachments: [await fileAttachment(invoiceFileID, fileName)],
    cc,
  });
}

export const ikhGenerateInvoice = registerMutation({
  type: () => Int,
  operationName: 'ikhGenerateInvoice',
  params: [
    { type: () => String, name: 'paymentID' },
    { type: () => Boolean, name: 'invoiceCorrection' },
  ],
  resolve: (paymentID: string, invoiceCorrection: boolean) => {
    return async () => {
      const payment = await runInContext(serverContext.sessionID, () =>
        getBHPaymentByID(paymentID)({
          _id: 1,
          orderNr: { year: 1, nr: 1 },
          participations: { _id: 1, userID: 1, product: { _id: 1, institutionID: 1 } },
        }),
      );
      if (!payment) throw new Error('payment not found');
      const orderNr = new BHIKHOrderNr(payment.orderNr);
      const userID = payment?.participations[0]?.userID;
      if (!userID) throw new Error('userID not found');
      const institutionID = payment?.participations[0]?.product?.institutionID;
      if (!institutionID) throw new Error('institutionID not found');
      if (!context.serverContext) {
        const currentUser = await getCurrentUserServer();
        const isPaymentOwner = currentUser?._id === userID;
        const mayManageParticipation = currentUser?.hasPermission(Permission.MANAGE_PARTICIPATION, institutionID);
        if (!isPaymentOwner && !mayManageParticipation) throw new Error('not authorized');
      }

      const user = await runInContext(serverContext.sessionID, () =>
        getUserByID(userID)({ ...selectionSet(() => User, 2), _id: 1, _class: 1, firstName: 1, lastName: 1, title: 1 }),
      );
      if (!user) throw new Error('user not found');

      const existingInvoice = await runInContext(serverContext.sessionID, () =>
        loadMongoData(IKHInvoice, { paymentID }).then(its => its[0]),
      );
      const invoiceID = existingInvoice?._id ?? A.getGloballyUniqueID();
      const createInvoiceBuffer = (component: string) =>
        printComponent(component, {
          user,
          timeout: 25 * 1000,
          queryParams: { payment: paymentID, isCorrection: invoiceCorrection },
        });

      const invoiceCustomer = await AdornisFile.createFromBuffer(
        await createInvoiceBuffer('ikh-invoice-customer'),
        buildIkhInvoiceFileName(orderNr, user.lastName, 'customer', invoiceCorrection),
      );
      const invoiceInternal = await AdornisFile.createFromBuffer(
        await createInvoiceBuffer('ikh-invoice-internal'),
        buildIkhInvoiceFileName(orderNr, user.lastName, 'internal', invoiceCorrection),
      );

      if (invoiceCorrection) {
        if (!existingInvoice)
          throw new Error(
            'no existing invoice found; can only create invoice-correction if normal invoice already exists',
          );
        const invoice = new IKHInvoice(existingInvoice);
        invoice.pdfCorrectionID = invoiceCustomer._id;
        invoice.pdfCorrectionInternalID = invoiceInternal._id;
        await runInContext(serverContext.sessionID, () => invoice.save());
        return;
      }

      const invoice = new IKHInvoice({
        _id: invoiceID,
        paymentID,
        pdfID: invoiceCustomer._id,
        pdfInternalID: invoiceInternal._id,
        userID: user._id,
        institutionID,
      });

      if (existingInvoice) await runInContext(serverContext.sessionID, () => invoice.save());
      else await runInContext(serverContext.sessionID, () => invoice.create());
    };
  },
});

export const sendIKHInvoiceMails = registerMutation({
  type: () => Boolean,
  operationName: 'sendIKHInvoiceMails',
  params: [
    { type: () => String, name: 'invoiceID' },
    { type: () => Boolean, name: 'isInvoiceCorrection' },
  ],
  resolve: (invoiceID: string, isInvoiceCorrection: boolean) => {
    return async () => {
      const invoice = await runInContext(serverContext.sessionID, () =>
        getIKHInvoiceByID(invoiceID)({
          _id: 1,
          institutionID: 1,
          paymentID: 1,
          pdfID: 1,
          pdfInternalID: 1,
          pdfCorrectionID: 1,
          pdfCorrectionInternalID: 1,
        }),
      );
      if (!invoice) throw new Error('invoice not found');
      if (!context.serverContext) {
        const currentUser = await getCurrentUserServer();
        if (!currentUser?.hasPermission(Permission.MANAGE_PARTICIPATION, invoice.institutionID))
          throw new Error('not authorized');
      }

      const payment = await runInContext(serverContext.sessionID, () =>
        getBHPaymentByID(invoice.paymentID)({
          _id: 1,
          createdAt: 1,
          participationIDs: 1,
          status: 1,
          orderNr: { year: 1, nr: 1 },
        }),
      );
      if (!payment) throw new Error('should not happen');
      const orderNr = new BHIKHOrderNr(payment.orderNr);

      const participations = await runInContext(serverContext.sessionID, () =>
        getParticipationsByID(payment.participationIDs)({
          _id: 1,
          userID: 1,
          user: {
            _id: 1,
            email: 1,
            firstName: 1,
            lastName: 1,
            title: 1,
            username: 1,
            whatsappNotification: 1,
            whatsappPhone: 1,
            suspentions: { _id: 1, institutionID: 1 },
            receivesNewsletter: 1,
          },
          productID: 1,
          createdAt: 1,
          updatedAt: 1,
          state: 1,
          isNoShow: 1,
          signoffDate: 1,
          registrationDate: 1,
          fulfilledParticipationRequirements: 1,
          lastStateChangeWasInitiatedByAdmin: 1,
          currentPayment: { status: 1, participationIDs: 1, orderNr: { year: 1, nr: 1 }, amount: 1, discount: 1 },
          productPriceForUser: { unitAmount: 1, currency: 1 },
          product: {
            _class: 1,
            _id: 1,
            name: 1,
            description: 1,
            translatedDescriptionHTML: { _class: 1, langKey: 1, text: 1 },
            sortingWeight: 1,
            hasPrice: 1,
            categoryID: 1,
            institutionID: 1,
            timeframeID: 1,
            timeframe: { from: 1, till: 1 },
            settings: {
              interAccountingEmail1: 1,
              interAccountingEmail2: 1,
              interAccountingEmail3: 1,
              inheritedInterAccountingEmail1: 1,
              inheritedInterAccountingEmail2: 1,
              inheritedInterAccountingEmail3: 1,
            },
            attachedCertificate: { _class: 1, certificateID: 1, exportDate: 1 },
            publishDate: 1,
            topic: 1,
            language: 1,
            presenceType: 1,
            isCanceled: 1,
            ...{
              appointments: { _class: 1, _id: 1, start: 1, end: 1, isNonBlocking: 1, location: 1, name: 1 },
              capacity: 1,

              hideCapacity: 1,
            },
          },
        }).then(ps => ps.filter(isDefined)),
      );

      const p = participations[0];
      if (!p) throw new Error('participations required');

      if (p.state === ParticipationState.waiting) {
        logger.debug('invoice not send because user is on waiting list');
        return false;
      }

      const user = p.user;
      if (!user) throw new Error('user is missing');

      const product = p.product;
      if (!product) throw new Error('product not found');

      const institutionID = product.institutionID;

      const stammdaten = await runInContext(serverContext.sessionID, async () => {
        const collection = await scopedCollection<IKHStammdaten>(UserStammdaten._class);
        return collection.findOne({ userID: user._id, institutionID });
      });
      if (!stammdaten) throw new Error('stammdaten not found');

      const invoiceCustomerLink = AdornisFile.getServeLink(
        isInvoiceCorrection ? invoice.pdfCorrectionID : invoice.pdfID,
      );
      const invoiceinternalLink = AdornisFile.getServeLink(
        isInvoiceCorrection ? invoice.pdfCorrectionInternalID : invoice.pdfInternalID,
      );
      const sendUserMail = (userMail: string, cc: Maybe<string> = null) =>
        notifiyIkhInvoice(
          institutionID,
          user,
          product,
          MailTemplateType.IKH_INVOICE_USER,
          invoiceCustomerLink,
          buildIkhInvoiceFileName(orderNr, user.lastName, 'customer', isInvoiceCorrection),
          userMail,
          cc,
          isInvoiceCorrection ? invoice.pdfCorrectionID : invoice.pdfID,
        );
      const sendInstitutionMail = (institutionMail: string) =>
        notifiyIkhInvoice(
          institutionID,
          user,
          product,
          MailTemplateType.IKH_INVOICE_INSTITUTION,
          invoiceinternalLink,
          buildIkhInvoiceFileName(orderNr, user.lastName, 'internal', isInvoiceCorrection),
          institutionMail,
          null,
          isInvoiceCorrection ? invoice.pdfCorrectionInternalID : invoice.pdfInternalID,
        );

      const isIntern = (stammdaten as any).invoiceReceiver === IKHInvoiceReceiverEnum.IKHINTERNAL;
      const isExtern = (stammdaten as any).invoiceReceiver === IKHInvoiceReceiverEnum.IKHEXTERNAL;

      // user mail
      if (isIntern)
        void 0; // do nothing
      else if (isExtern) await sendUserMail(user.email, stammdaten.institutionEmail);
      else await sendUserMail(user.email);

      // institution mail
      await (async () => {
        const s = p.product?.settings;
        if (!s) {
          logger.warn('no settings found; cant send mail');
          return;
        }
        const institutionMail1 = s.interAccountingEmail1 ?? s.inheritedInterAccountingEmail1?.[1];
        const institutionMail2 = s.interAccountingEmail2 ?? s.inheritedInterAccountingEmail2?.[1];
        const institutionMail3 = s.interAccountingEmail3 ?? s.inheritedInterAccountingEmail3?.[1];
        const institutionMails = [institutionMail1, institutionMail2, institutionMail3]
          .filter(it => !!it)
          .filter(isDefined);
        if (institutionMails.length === 0) {
          logger.warn('no institution-mail available; cant send institution-invoice-copy');
          return;
        }

        for (const institutionMail of institutionMails) {
          await sendInstitutionMail(institutionMail);
        }
      })();

      if (!isInvoiceCorrection) {
        payment.status = 'issued';
        await runInContext(serverContext.sessionID, () => payment.save());
      }

      return true;
    };
  },
});

export const paymentPluginIkhPay = registerMutation({
  type: () => [String],
  operationName: 'paymentPluginIkhPay',
  params: [
    { type: () => String, name: 'institutionID' },
    { type: () => [String], name: 'participationIds' },
  ],
  resolve: (institutionID: string, participationIDs: string[]) => {
    return async () => {
      const participations = (await getParticipationsByID(participationIDs)({
        _id: 1,
        userID: 1,
        user: {
          _id: 1,
          email: 1,
          firstName: 1,
          lastName: 1,
          title: 1,
          username: 1,
          whatsappNotification: 1,
          whatsappPhone: 1,
          suspentions: { _id: 1, institutionID: 1 },
          receivesNewsletter: 1,
        },
        productID: 1,
        product: { _id: 1, name: 1, institutionID: 1, hasPrice: 1, prices: { unitAmount: 1, currency: 1 } },
        createdAt: 1,
        updatedAt: 1,
        state: 1,
        isNoShow: 1,
        signoffDate: 1,
        registrationDate: 1,
        fulfilledParticipationRequirements: 1,
        lastStateChangeWasInitiatedByAdmin: 1,
        currentPayment: { status: 1, participationIDs: 1, orderNr: { year: 1, nr: 1 }, amount: 1, discount: 1 },
        productPriceForUser: { unitAmount: 1, currency: 1 },
      })) as Participation[];
      const availableBundles = await getAllBundlesByInstitution(institutionID)({
        _id: 1,
        _class: 1,
        name: 1,
        productIDs: 1,
        isActive: 1,
      });

      const user = await getCurrentUserServer();
      if (!user) throw new Error('user should be loggedIn');

      // create Payment
      const stammdaten = await getStammdatenByUserAndInstitution(user._id, institutionID)(IKHStammdaten.allFields);
      if (!stammdaten) throw new Error('stammdaten not found userID=' + user._id + ', institutionID=' + institutionID);

      // send of mail per participation
      const bestBundling = getProductsBestBundled(
        participations.map(pa => pa.product!).filter(p => !!p),
        availableBundles,
      );

      const paymentIDs = [] as string[];
      for (const curBooked of bestBundling) {
        let curBillParti: Participation[];
        if (curBooked instanceof Product) curBillParti = [participations.find(pa => pa.productID === curBooked._id)!];
        else curBillParti = participations.filter(pa => curBooked.products.find(p => p._id === pa.productID));

        const bundleID = curBooked instanceof Product ? undefined : curBooked.bundle._id;
        const payment = await createPaymentFromParticipations(institutionID, curBillParti, (productID, userID) =>
          getProductPrice(productID, userID, bundleID),
        );
        ((payment.orderNr = await BHIKHOrderNr.create(institutionID)), (payment.userReference.userID = user._id));

        const paymentID = await payment.create();
        logger.info({ paymentID }, '(ikh) payment created');
        paymentIDs.push(paymentID);

        // create invoice
        await ikhGenerateInvoice(paymentID, false)();
      }

      // send Mails
      await notifyBestaetigungWorkshopRegistrierung(institutionID, user, participations);

      return paymentIDs;
    };
  },
});

export const getIKHInvoicesByIDs = registerQuery({
  operationName: 'getIKHInvoicesByIDs',
  type: () => [IKHInvoice],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<IKHInvoice>>(gqlFields: SelSet) =>
      readEntitiesByIDs(IKHInvoice, ids, gqlFields),
});

export const subscribeIKHInvoicesByIDs = registerSubscription({
  operationName: 'subscribeIKHInvoicesByIDs',
  type: () => [IKHInvoice],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<IKHInvoice>>(gqlFields: SelSet) =>
      watchEntitiesByIDs(IKHInvoice, ids, gqlFields),
});

export const getAllIKHInvoices = registerQuery({
  operationName: 'getAllIKHInvoices',
  type: () => [IKHInvoice],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<IKHInvoice>>(gqlFields: SelSet) =>
      readAllEntities(IKHInvoice, gqlFields, { skip, limit, excludeIDs }),
});

export const subscribeAllIKHInvoices = registerSubscription({
  operationName: 'subscribeAllIKHInvoices',
  type: () => [IKHInvoice],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<IKHInvoice>>(gqlFields: SelSet) =>
      watchAllEntities(IKHInvoice, gqlFields, { skip, limit, excludeIDs }),
});

export const getIKHInvoiceByID =
  (id: string | null | undefined) =>
  async <SelSet extends BaseQLSelectionSet<IKHInvoice>>(gqlFields: SelSet) => {
    if (!id) return null;
    return (await getIKHInvoicesByIDs([id])(gqlFields))[0] ?? null;
  };

export const subscribeIKHInvoiceByID =
  (id: string | null | undefined) =>
  <SelSet extends BaseQLSelectionSet<IKHInvoice>>(gqlFields: SelSet) =>
    id ? subscribeIKHInvoicesByIDs([id])(gqlFields).pipe(map(entities => entities[0] ?? null)) : of(null);
